import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

let isInitialized = false;

export function initSentry() {
    if (isInitialized || !SENTRY_DSN) {
        if (!SENTRY_DSN) {
            console.warn("[Sentry] VITE_SENTRY_DSN not set, error tracking disabled");
        }
        return;
    }

    Sentry.init({
        dsn: SENTRY_DSN,
        environment: import.meta.env.MODE,

        // Performance monitoring
        tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

        // Session replay for errors
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,

        // Integrations
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],

        // Filter out non-critical errors
        beforeSend(event) {
            // Ignore errors from extensions
            if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
                frame => frame.filename?.includes("chrome-extension")
            )) {
                return null;
            }
            return event;
        },
    });

    isInitialized = true;
    console.log("[Sentry] Initialized successfully");
}

// Set user context after login
export function setSentryUser(userId: string, email?: string) {
    if (!isInitialized) return;

    Sentry.setUser({ id: userId, email });
}

// Clear user on logout
export function clearSentryUser() {
    if (!isInitialized) return;

    Sentry.setUser(null);
}

// Capture error with context
export function captureError(error: Error, context?: Record<string, any>) {
    if (!isInitialized) {
        console.error("[Sentry disabled]", error, context);
        return;
    }

    Sentry.captureException(error, {
        extra: context,
    });
}

// Add breadcrumb for debugging
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
    if (!isInitialized) return;

    Sentry.addBreadcrumb({
        message,
        category,
        data,
        level: "info",
    });
}

// Wrap async operations for better error tracking
export async function withSentrySpan<T>(
    name: string,
    operation: () => Promise<T>
): Promise<T> {
    if (!isInitialized) return operation();

    return Sentry.startSpan({ name, op: "function" }, async () => {
        return await operation();
    });
}

export { Sentry };
